/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState } from 'react';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import axiosCall from '../../services/axios';
import { getErrorFromEndpoint, isObjEmptyOrNull, isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLoanContext } from '../../contexts/LoanContext';

const useESignDocTrackingList = () => {
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const { get } = useAbortController();
  const queryClient = useQueryClient();
  const [ESignDocTrackingListResp, setESignDocTrackingListResp] = useState(null);
  const [eSignDocTrackingErrors, setESignDocTrackingErrors] = useState('');
  const [eSignDocTrackingLoading, setESignDocTrackingLoading] = useState(false);
  const queryKey = useMemo(() => ['esign doc tracking list'], []);

  const ESignDocTrackingListAPI = useCallback(async (): Promise<UnknownESignDocTrackingListResponse> => {
    const urlPath = process.env.REACT_APP_ESIGN_DOC_TRACKING_LIST.replace('?', loanNumber);
    const request = { url: urlPath, method: 'get' };
    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };
    const config = get();

    const res = await axiosCall(request, config, headers)
      .then((res) => {
        if (!isObjEmptyOrNull(res)) {
          const err = getErrorFromEndpoint(res);
          if (isStrEmptyOrNull(err)) {
            setESignDocTrackingListResp(res);
            return res;
          } else {
            console.log(err);
            setESignDocTrackingErrors(err);
            setESignDocTrackingListResp(null);
            return null;
          }
        }
        return null;
      })
      .catch((e: Error) => {
        console.log('Error using ESignDocTrackingListAPI: ', e);
        setESignDocTrackingErrors(e.message);
        setESignDocTrackingListResp(null);
        return null;
      });
    setESignDocTrackingLoading(false);
    return res;
  }, [get, token]);

  const processESignDocTrackingList = useCallback(async (): Promise<UnknownESignDocTrackingListResponse> => {
    setESignDocTrackingListResp(null);
    setESignDocTrackingLoading(true);
    const res = await ESignDocTrackingListAPI();
    console.log('Res', res);
    return res;
  }, [ESignDocTrackingListAPI]);

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        const response = await processESignDocTrackingList();
        //console.log('response in esigndoctrackinglist', response);
        return { data: response };
      } catch (error) {
        console.error(`useESignDocTrackingList.caught: ${error.message}`);
        return { data: null };
      }
    },
  });

  //console.log('data in esigndoctrackinglist', data);

  const reloadESignDocTrackingList = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  return {
    ESignDocTrackingListResp,
    eSignDocTrackingLoading,
    eSignDocTrackingErrors,
    processESignDocTrackingList,
    esignDocTrackingList: data?.data ? data.data : null,
    reloadESignDocTrackingList,
    isFetchingESignDocTrackingList: isFetching,
  };
};

export default useESignDocTrackingList;
